/**
 * Finance helpers, copied from the design's fnAithoraVals, fnFacilityVals and financeVals
 * (Aithora ConsoleV3.dc.html L23010-23217, L23571-24227): its badges, sparkline, money
 * formats, buttons and row entrance. Every figure comes from the API.
 */
import { honestyOf, type ApiClient, type ApiError } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery, type QueryState } from '../../useQuery';

export const pre = (e?: { preventDefault?: () => void }) => e?.preventDefault?.();

export function useFinanceApi<T>(load: (client: ApiClient, signal: AbortSignal) => Promise<T>, deps: unknown[] = [], enabled = true): QueryState<T> {
  const { client, session } = useSession();
  return useQuery<T>((signal) => load(client, signal), [client, session?.token, ...deps], { enabled });
}
export const notServed = (e: ApiError | null | undefined) => !!e && e.kind === 'not_found';

/** The design's badge words (design BADGE, L23019). */
export type BadgeWord = 'LIVE' | 'MODELED' | 'ADMIN-SET' | 'NOT WIRED' | 'SAMPLE' | 'ESTIMATED';
const BADGE: Record<BadgeWord, [string, string]> = {
  LIVE: ['#1F6B52', 'rgba(31,107,82,.12)'],
  MODELED: ['#264C7E', 'rgba(47,92,151,.12)'],
  'ADMIN-SET': ['#3C454F', 'rgba(34,39,46,.08)'],
  'NOT WIRED': ['#B3261E', 'rgba(179,38,30,.08)'],
  SAMPLE: ['#54606C', 'rgba(34,39,46,.07)'],
  ESTIMATED: ['#54606C', 'rgba(34,39,46,.07)'],
};
export const bs = (b: BadgeWord) =>
  'font-size:13px;font-weight:800;letter-spacing:.07em;border-radius:4px;padding:1px 5px;white-space:nowrap;color:' + BADGE[b][0] + ';background:' + BADGE[b][1] + ';';

/**
 * A figure's badge from its own flags: admin-entered reads ADMIN-SET, measured LIVE,
 * modeled MODELED, sample SAMPLE. A figure the back end does not serve reads NOT WIRED.
 */
export function badgeOf(f: { basis?: string | null; origin?: string | null } | null | undefined, real?: boolean): BadgeWord {
  if (!f) return 'NOT WIRED';
  if (f.origin === 'admin_set') return 'ADMIN-SET';
  const l = honestyOf(real === undefined ? null : { real }, { basis: f.basis ?? null });
  return l === 'MEASURED' ? 'LIVE' : l === 'MODELED' ? 'MODELED' : l === 'ESTIMATED' ? 'ESTIMATED' : l === 'SAMPLE' ? 'SAMPLE' : 'NOT WIRED';
}

/** A 100x24 sparkline's points (design spark). */
export function spark(a: number[]): string {
  if (a.length < 2) return '';
  const mn = Math.min(...a);
  const mx = Math.max(...a);
  const r = mx - mn || 1;
  return a.map((v, i) => ((i * 100) / (a.length - 1)).toFixed(1) + ',' + (22 - ((v - mn) / r) * 20).toFixed(1)).join(' ');
}

/** $K to "$5.20M" or "$417K" (design m$). */
export const mK = (k: number) => (k >= 1000 ? '$' + (k / 1000).toFixed(2) + 'M' : '$' + Math.round(k) + 'K');
/** Dollars to "$48.2K" (one decimal in thousands). */
export const k1 = (usd: number) => (Math.abs(usd) >= 1000 ? '$' + (usd / 1000).toFixed(1) + 'K' : '$' + Math.round(usd));
/** Cents to "$4,380". */
export const whole = (cents: number) => '$' + Math.round(cents / 100).toLocaleString('en-US');

/** A button (design btnS): primary blue, or outlined. */
export const btnS = (primary: boolean) =>
  'display:inline-flex;align-items:center;justify-content:center;min-height:32px;padding:0 11px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
  (primary ? 'background:#2F5C97;color:#fff;' : 'border:1px solid rgba(47,92,151,.4);color:#264C7E;');
/** An Act now row (design rowS). */
export const rowS = (i: number, isDone: boolean) =>
  'animation:riseIn .3s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 50 + 'ms;display:flex;align-items:center;flex-wrap:wrap;gap:6px 12px;padding:6px 0;border-bottom:1px solid rgba(34,39,46,.08);' +
  (isDone ? 'opacity:.6;' : '');
/** A tone to its colour. */
export const TONE: Record<'red' | 'green' | 'grey', string> = { red: '#B3261E', green: '#1F6B52', grey: '#3C454F' };

/** The pill tabs (design pill) and the scope segments (design seg). */
export const pill = (on: boolean) =>
  'display:inline-flex;align-items:center;height:32px;padding:0 15px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (on ? 'background:rgba(79,138,214,.15);color:#2F5C97;' : 'background:transparent;color:#54606C;');
export const seg = (on: boolean) =>
  'display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:6px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (on ? 'background:#2F5C97;color:#fff;' : 'background:transparent;color:#54606C;');
/** A chip (design chip). */
export const chip = (fg: string, bg: string) =>
  'font-size:14px;font-weight:700;letter-spacing:.09em;border-radius:6px;padding:3px 9px;flex:0 0 auto;white-space:nowrap;color:' + fg + ';background:' + bg + ';';
/** A big figure (design fig). */
export const figS = (c: string, px = 26) => 'font-size:' + px + 'px;font-weight:700;line-height:1;letter-spacing:-.015em;color:' + c + ';';
export const CAPT = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
export const cellS = (align?: string, weight?: number, color?: string) =>
  // V9 (design cell, L26801): 16px, and long text wraps instead of being cut short.
  'font-size:16px;' + (align ? 'text-align:' + align + ';' : '') + 'font-weight:' + (weight || 400) + ';color:' + (color || '#22272E') + ';overflow-wrap:break-word;';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "Mar 2026" for a month index from "YYYY-MM" plus k months. */
export function monthLabel(start: string, k: number): string {
  const [y, m] = start.split('-').map(Number);
  const d = m - 1 + k;
  return MONTHS[((d % 12) + 12) % 12] + ' ' + (y + Math.floor(d / 12));
}
/** "Sep" for "2026-09". */
export const shortMonth = (ym: string) => MONTHS[Number(ym.slice(5, 7)) - 1] ?? ym;
/** The month before now, "YYYY-MM". */
/** This calendar month, YYYY-MM (the month in progress). */
export function currentPeriod(now: number): string {
  const d = new Date(now);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}
export function previousPeriod(now: number): string {
  const d = new Date(now);
  const p = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  return p.getFullYear() + '-' + String(p.getMonth() + 1).padStart(2, '0');
}
/** Whole days between an ISO time and now. */
export const daysSince = (iso: string | null | undefined, now: number) => (iso ? Math.max(0, Math.floor((now - Date.parse(iso)) / 86_400_000)) : null);

/** Download a file from an authenticated path (board pack, ledger). */
export async function downloadFrom(client: ApiClient, path: string, filename: string): Promise<boolean> {
  const res = await client.raw(path);
  if (!res.ok) return false;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return true;
}
